import { all, fork, put, takeEvery, call } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/core';

// apicore
import { APICore, setAuthorization } from 'src/helpers/api/apiCore';

// api
import {
    createAssociatedList as createAssociatedListApi,
    updateAssociatedList as updateAssociatedListApi,
    getAllAssociatedList as getAllAssociatedListApi,
    getAssociatedList as getAssociatedListApi,
    getDrListInst as getDrListInstApi,
    getAllDrList as getAllDrListApi,
} from './api';

// actions
import { associatedListApiResponseSuccess, associatedListApiResponseError } from './actions';

// constants
import { AssociatedTypeActionTypes } from './constants';

type AssociatedListData = {
    payload: any;
    type: string;
};

const api = new APICore();

/**
 * Add Associated List
 * @param {*} payload
 */
function* AddAssociatedList({ payload, type }: AssociatedListData): SagaIterator {
    try {
        const response = yield call(createAssociatedListApi, payload);
        const { data, message, success, code } = response.data;
        if (code === 200 && success) {
            yield put(
                associatedListApiResponseSuccess(
                    AssociatedTypeActionTypes.ADD_ASSOCIATED_LIST,
                    data,
                    'Associated List has been created.',
                    code,
                    false,
                    true
                )
            );
        } else {
            let msg = `An error while creating Associated List!`;
            if (message) msg = message;
            yield put(
                associatedListApiResponseSuccess(
                    AssociatedTypeActionTypes.ADD_ASSOCIATED_LIST,
                    data,
                    msg,
                    code,
                    true,
                    false
                )
            );
        }
    } catch (error: any) {
        yield put(
            associatedListApiResponseError(AssociatedTypeActionTypes.ADD_ASSOCIATED_LIST, [], error, 404, true, false)
        );
    }
}

/**
 * Edit Associated List
 * @param {*} payload
 */
function* EditAssociatedList({ payload, type }: AssociatedListData): SagaIterator {
    try {
        const response = yield call(updateAssociatedListApi, payload);
        const { data, message, success, code } = response.data;
        if (code === 200 && success) {
            yield put(
                associatedListApiResponseSuccess(
                    AssociatedTypeActionTypes.EDIT_ASSOCIATED_LIST,
                    data,
                    'Associated List Get Updated Successfully',
                    code,
                    false,
                    true
                )
            );
        } else {
            let msg = `An error while edit Associated List!`;
            if (message) msg = message;
            yield put(
                associatedListApiResponseSuccess(
                    AssociatedTypeActionTypes.EDIT_ASSOCIATED_LIST,
                    data,
                    msg,
                    code,
                    true,
                    false
                )
            );
        }
    } catch (error: any) {
        yield put(
            associatedListApiResponseError(AssociatedTypeActionTypes.EDIT_ASSOCIATED_LIST, [], error, 404, true, false)
        );
    }
}

/**
 * Fetch Associated List
 * @param {*} payload
 */
function* getAssociatedList({ payload, type }: AssociatedListData): SagaIterator {
    try {
        const response = yield call(getAssociatedListApi, payload);
        const { data, message, success, code } = response.data;
        if (code === 200 && success) {
            yield put(
                associatedListApiResponseSuccess(
                    AssociatedTypeActionTypes.GET_ASSOCIATED_LIST,
                    data,
                    message,
                    code,
                    false,
                    false
                )
            );
        } else {
            let msg = `An error while getting Associated List!`;
            if (message) msg = message;
            yield put(
                associatedListApiResponseSuccess(
                    AssociatedTypeActionTypes.GET_ASSOCIATED_LIST,
                    data,
                    msg,
                    code,
                    true,
                    false
                )
            );
        }
    } catch (error: any) {
        yield put(
            associatedListApiResponseError(AssociatedTypeActionTypes.GET_ASSOCIATED_LIST, [], error, 404, true, false)
        );
    }
}

/**
 * Fetch Associated Lists
 * @param {*} payload
 */
function* getAssociatedLists({ payload, type }: AssociatedListData): SagaIterator {
    try {
        const response = yield call(getAllAssociatedListApi, payload);
        const { data, message, success, code } = response.data;
        const responseData = typeof data === 'object' && Object.keys(data).length === 0 ? [] : data;
        if (code === 200 && success) {
            yield put(
                associatedListApiResponseSuccess(
                    AssociatedTypeActionTypes.GET_ASSOCIATED_LISTS,
                    responseData,
                    message,
                    code,
                    false,
                    false
                )
            );
        } else {
            let msg = `An error while getting Associated List Listing!`;
            if (message) msg = message;
            yield put(
                associatedListApiResponseSuccess(
                    AssociatedTypeActionTypes.GET_ASSOCIATED_LISTS,
                    responseData,
                    msg,
                    code,
                    true,
                    false
                )
            );
        }
    } catch (error: any) {
        yield put(
            associatedListApiResponseError(AssociatedTypeActionTypes.GET_ASSOCIATED_LISTS, [], error, 404, true, false)
        );
    }
}

/**
 * Fetch Associated Inst Lists
 * @param {*} payload
 */
function* getAssociatedInstLists({ payload, type }: AssociatedListData): SagaIterator {
    try {
        const response = yield call(getAllAssociatedListApi, payload);
        const { data, message, success, code } = response.data;
        const responseData = typeof data === 'object' && Object.keys(data).length === 0 ? [] : data;
        if (code === 200 && success) {
            yield put(
                associatedListApiResponseSuccess(
                    AssociatedTypeActionTypes.GET_ASSOCIATED_INST_LISTS,
                    responseData,
                    message,
                    code,
                    false,
                    false
                )
            );
        } else {
            let msg = `An error while getting Associated Institute List Listing!`;
            if (message) msg = message;
            yield put(
                associatedListApiResponseSuccess(
                    AssociatedTypeActionTypes.GET_ASSOCIATED_INST_LISTS,
                    responseData,
                    msg,
                    code,
                    true,
                    false
                )
            );
        }
    } catch (error: any) {
        yield put(
            associatedListApiResponseError(
                AssociatedTypeActionTypes.GET_ASSOCIATED_INST_LISTS,
                [],
                error,
                404,
                true,
                false
            )
        );
    }
}

/**
 * Fetch Refer Doctor Lists
 * @param {*} payload
 */
function* getReferDrInstLists({ payload, type }: AssociatedListData): SagaIterator {
    try {
        const response = yield call(getDrListInstApi, payload);
        const { data, message, success, code } = response.data;
        const responseData = typeof data === 'object' && Object.keys(data).length === 0 ? [] : data;
        if (code === 200 && success) {
            yield put(
                associatedListApiResponseSuccess(
                    AssociatedTypeActionTypes.GET_REFER_DR_LISTS,
                    responseData,
                    message,
                    code,
                    false,
                    false
                )
            );
        } else {
            let msg = `An error while getting Refer Doctor List Listing!`;
            if (message) msg = message;
            yield put(
                associatedListApiResponseSuccess(
                    AssociatedTypeActionTypes.GET_REFER_DR_LISTS,
                    responseData,
                    msg,
                    code,
                    true,
                    false
                )
            );
        }
    } catch (error: any) {
        yield put(
            associatedListApiResponseError(AssociatedTypeActionTypes.GET_REFER_DR_LISTS, [], error, 404, true, false)
        );
    }
}

/**
 * Fetch User Refer Doctor Lists
 * @param {*} payload
 */
function* getUserReferDrLists({ payload, type }: AssociatedListData): SagaIterator {
    try {
        const response = yield call(getAllDrListApi, payload);
        const { data, message, success, code } = response.data;
        const responseData = typeof data === 'object' && Object.keys(data).length === 0 ? [] : data;
        if (code === 200 && success) {
            yield put(
                associatedListApiResponseSuccess(
                    AssociatedTypeActionTypes.GET_USER_REFER_DR_LISTS,
                    responseData,
                    message,
                    code,
                    false,
                    false
                )
            );
        } else {
            let msg = `An error while getting User Refer Doctor List Listing!`;
            if (message) msg = message;
            yield put(
                associatedListApiResponseSuccess(
                    AssociatedTypeActionTypes.GET_USER_REFER_DR_LISTS,
                    responseData,
                    msg,
                    code,
                    true,
                    false
                )
            );
        }
    } catch (error: any) {
        yield put(
            associatedListApiResponseError(
                AssociatedTypeActionTypes.GET_USER_REFER_DR_LISTS,
                [],
                error,
                404,
                true,
                false
            )
        );
    }
}

export function* watchGetAssociatedList() {
    yield takeEvery(AssociatedTypeActionTypes.GET_ASSOCIATED_LIST, getAssociatedList);
}

export function* watchAddAssociatedList() {
    yield takeEvery(AssociatedTypeActionTypes.ADD_ASSOCIATED_LIST, AddAssociatedList);
}

export function* watchEditAssociatedList(): any {
    yield takeEvery(AssociatedTypeActionTypes.EDIT_ASSOCIATED_LIST, EditAssociatedList);
}

export function* watchGetAssociatedLists(): any {
    yield takeEvery(AssociatedTypeActionTypes.GET_ASSOCIATED_LISTS, getAssociatedLists);
}

export function* watchGetAssociatedInstLists(): any {
    yield takeEvery(AssociatedTypeActionTypes.GET_ASSOCIATED_INST_LISTS, getAssociatedInstLists);
}

export function* watchGetReferDrInstLists(): any {
    yield takeEvery(AssociatedTypeActionTypes.GET_REFER_DR_LISTS, getReferDrInstLists);
}

export function* watchGetUserReferDrInstLists(): any {
    yield takeEvery(AssociatedTypeActionTypes.GET_USER_REFER_DR_LISTS, getUserReferDrLists);
}

function* associatedListSaga() {
    yield all([
        fork(watchGetAssociatedList),
        fork(watchAddAssociatedList),
        fork(watchEditAssociatedList),
        fork(watchGetAssociatedInstLists),
        fork(watchGetAssociatedLists),
        fork(watchGetReferDrInstLists),
        fork(watchGetUserReferDrInstLists),
    ]);
}

export default associatedListSaga;
